class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.setCartTriggersAccessibility();
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
  }

  setCartTriggersAccessibility() {
    const openDrawer = (event, trigger) => {
      event.preventDefault();
      this.open(trigger);
    };

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink && !cartLink.dataset.drawerBound) {
      cartLink.dataset.drawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => openDrawer(event, cartLink));
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          openDrawer(event, cartLink);
        }
      });
    }

    document.querySelectorAll('.mobile-footer-menu a[href="/cart"], .mobile-footer-menu a[href*="cart"]').forEach((link) => {
      if (link.dataset.drawerBound) return;
      link.dataset.drawerBound = 'true';
      link.addEventListener('click', (event) => openDrawer(event, link));
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHtml) return;
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    }));

    const cartErrors = document.getElementById('CartDrawer-CartErrors');
    if (cartErrors) cartErrors.textContent = '';

    setTimeout(() => {
      this.bindOverlayClose();
      this.setCartTriggersAccessibility();
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          if (typeof window.updateCartOffersProgress === 'function') {
            window.updateCartOffersProgress(cart);
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        })
        .catch(() => {});
      this.open();
    });
  }

  connectedCallback() {
    this.setCartTriggersAccessibility();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector) || doc.body.firstElementChild;
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-drawer-items' || event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  onCartUpdate() {
    const drawer = this.closest('cart-drawer');
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return;

    const sectionIds = drawer.getSectionsToRender().map((section) => section.id).join(',');

    fetch(`${window.location.pathname}?sections=${sectionIds}`)
      .then((response) => response.json())
      .then((sections) => {
        drawer.getSectionsToRender().forEach((section) => {
          const sectionHtml = sections[section.id];
          if (!sectionHtml) return;
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement) return;
          sectionElement.innerHTML = drawer.getSectionInnerHTML(sectionHtml, section.selector);
        });
        drawer.bindOverlayClose();
        drawer.setCartTriggersAccessibility();
        return fetch('/cart.js');
      })
      .then((response) => response && response.json())
      .then((cart) => {
        if (!cart) return;
        drawer.classList.toggle('is-empty', cart.item_count === 0);
        if (typeof window.updateCartOffersProgress === 'function') {
          window.updateCartOffersProgress(cart);
        }
      })
      .catch(() => {});
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
